import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactDOM from "react-dom";

/**
 * D2IntegrationDiagram
 * ─────────────────────────────────────────────────────────────────────────────
 * Renders a D2-style integration diagram in the Agent Chat final card.
 *
 * - SVG renders INSTANTLY (no loading spinner)
 * - D2 WASM silently upgrades in background
 * - Handles edge ID mismatch (label vs id)
 * - Auto-connects isolated nodes by type order (trigger→source→destination)
 * - Fullscreen modal via React portal
 */

const D2_NAVY = "#23346e";
const BOX_W = 160;   // wider boxes for longer platform names
const BOX_H = 50;
const LAYER_GAP = 90;    // vertical gap between layers
const NODE_GAP = 60;    // horizontal gap between sibling nodes
const PAD_X = 50;
const PAD_Y = 30;

const TYPE_ORDER = { trigger: 0, source: 1, action: 2, destination: 3 };
const getOrder = (n) => TYPE_ORDER[n?.type] ?? 2;

// ─── Edge ID normalizer ───────────────────────────────────────────────────────
// Edges from backend may use labels as references instead of node IDs.
// This resolves every edge endpoint to the canonical node.id.
function resolveEdgeIds(nodes, edges) {
  const lookup = new Map();
  nodes.forEach((n) => {
    // Register by id, id.toLowerCase, label, label.toLowerCase
    [n.id, n.id?.toLowerCase(), n.label, n.label?.toLowerCase()]
      .filter(Boolean)
      .forEach((k) => { if (!lookup.has(k)) lookup.set(k, n.id); });
  });

  const resolve = (r) =>
    r ? (lookup.get(r) || lookup.get(r.toLowerCase?.()) || r) : r;

  return edges
    .map((e) => ({ ...e, from: resolve(e.from), to: resolve(e.to) }))
    .filter((e) => e.from && e.to && e.from !== e.to);
}

// ─── Isolated-node auto-connector ────────────────────────────────────────────
// If a node participates in zero edges (backend omitted it or IDs mismatched),
// auto-connect it to its logical neighbour by type order.
function autoConnect(nodes, edges) {
  const connected = new Set(edges.flatMap((e) => [e.from, e.to]));
  const isolated = nodes.filter((n) => !connected.has(n.id));
  if (!isolated.length) return edges;

  const sorted = [...nodes].sort((a, b) => getOrder(a) - getOrder(b));
  const result = [...edges];

  isolated.forEach((iso) => {
    const iOrd = getOrder(iso);

    // Prefer connecting to an already-connected neighbour
    const prevC = sorted
      .filter((n) => n.id !== iso.id && getOrder(n) < iOrd && connected.has(n.id))
      .pop();
    const nextC = sorted.find(
      (n) => n.id !== iso.id && getOrder(n) > iOrd && connected.has(n.id)
    );

    if (prevC) {
      result.push({ from: prevC.id, to: iso.id });
    } else if (nextC) {
      result.push({ from: iso.id, to: nextC.id });
    } else {
      // No connected neighbours at all — just chain by type order
      const prev = sorted.filter((n) => n.id !== iso.id && getOrder(n) < iOrd).pop();
      const next = sorted.find((n) => n.id !== iso.id && getOrder(n) > iOrd);
      if (prev) result.push({ from: prev.id, to: iso.id });
      else if (next) result.push({ from: iso.id, to: next.id });
    }

    connected.add(iso.id);
  });

  return result;
}

// ─── Normalise a full graph (nodes + edges) ───────────────────────────────────
function normaliseGraph(graph) {
  if (!graph?.nodes?.length) return graph;

  const nodes = graph.nodes;
  let edges = resolveEdgeIds(nodes, graph.edges || []);

  // If NO edges at all, auto-chain in type order
  if (!edges.length) {
    const sorted = [...nodes].sort((a, b) => getOrder(a) - getOrder(b));
    for (let i = 0; i < sorted.length - 1; i++) {
      edges.push({ from: sorted[i].id, to: sorted[i + 1].id });
    }
  } else {
    edges = autoConnect(nodes, edges);
  }

  return { nodes, edges };
}

// ─── D2 Script Builder ────────────────────────────────────────────────────────
function buildD2Script(graph) {
  if (!graph?.nodes?.length) return "";
  const { nodes, edges } = graph;
  const sid = (s) =>
    (s || "node").replace(/[^a-zA-Z0-9_]/g, "_").replace(/^(\d)/, "_$1");

  const lines = [];
  nodes.forEach((n) => lines.push(`${sid(n.id || n.label)}: "${n.label}"`));
  edges.forEach((e) => lines.push(`${sid(e.from)} -> ${sid(e.to)}`));
  return lines.join("\n");
}

// ─── Layout Engine ────────────────────────────────────────────────────────────
function layoutGraph(nodes, edges) {
  if (!nodes?.length) return { positions: {}, svgW: 300, svgH: 200 };

  const ids = nodes.map((n) => n.id);
  const outMap = {}, inMap = {};
  ids.forEach((id) => { outMap[id] = []; inMap[id] = []; });
  edges.forEach((e) => {
    if (outMap[e.from] !== undefined) outMap[e.from].push(e.to);
    if (inMap[e.to] !== undefined) inMap[e.to].push(e.from);
  });

  // DFS topological sort
  const visited = new Set(), topo = [];
  const dfs = (id) => {
    if (visited.has(id)) return;
    visited.add(id);
    (outMap[id] || []).forEach(dfs);
    topo.unshift(id);
  };
  ids.forEach(dfs);

  // Longest-path layer assignment
  const layer = {};
  ids.forEach((id) => { layer[id] = 0; });
  topo.forEach((id) => {
    (outMap[id] || []).forEach((next) => {
      layer[next] = Math.max(layer[next] ?? 0, (layer[id] ?? 0) + 1);
    });
  });

  const numLayers = Math.max(...Object.values(layer)) + 1;
  const groups = Array.from({ length: numLayers }, () => []);
  ids.forEach((id) => groups[layer[id] ?? 0].push(id));

  const maxPerLayer = Math.max(...groups.map((g) => g.length));
  const svgW = Math.max(320, maxPerLayer * BOX_W + (maxPerLayer - 1) * NODE_GAP + PAD_X * 2);
  const svgH = numLayers * BOX_H + (numLayers - 1) * LAYER_GAP + PAD_Y * 2;

  const positions = {};
  groups.forEach((group, li) => {
    const totalW = group.length * BOX_W + (group.length - 1) * NODE_GAP;
    const startCX = (svgW - totalW) / 2 + BOX_W / 2;
    group.forEach((id, i) => {
      positions[id] = {
        cx: startCX + i * (BOX_W + NODE_GAP),
        cy: PAD_Y + li * (BOX_H + LAYER_GAP) + BOX_H / 2,
      };
    });
  });

  return { positions, svgW, svgH };
}

// ─── SVG Fallback ─────────────────────────────────────────────────────────────
function SVGFallback({ graph }) {
  if (!graph?.nodes?.length) return null;
  const { nodes, edges } = graph;
  const { positions, svgW, svgH } = layoutGraph(nodes, edges);

  return (
    <svg
      width={svgW}
      height={svgH}
      viewBox={`0 0 ${svgW} ${svgH}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", maxWidth: "100%", height: "auto", margin: "0 auto" }}
    >
      <defs>
        <marker id="d2arr" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill={D2_NAVY} />
        </marker>
        <filter id="nshadow" x="-8%" y="-8%" width="116%" height="130%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#00000012" />
        </filter>
      </defs>

      {/* Edges */}
      {edges.map((e, i) => {
        const from = positions[e.from];
        const to = positions[e.to];
        if (!from || !to) return null;          // should never happen after normalisation

        const x1 = from.cx, y1 = from.cy + BOX_H / 2;
        const x2 = to.cx, y2 = to.cy - BOX_H / 2 - 8;
        const midDY = (y2 - y1) / 2;
        const isStraight = Math.abs(x1 - x2) < 3;
        const d = isStraight
          ? `M ${x1} ${y1} L ${x2} ${y2}`
          : `M ${x1} ${y1} C ${x1} ${y1 + midDY}, ${x2} ${y2 - midDY}, ${x2} ${y2}`;

        return (
          <path key={`e-${i}`} d={d} fill="none"
            stroke={D2_NAVY} strokeWidth="1.5" markerEnd="url(#d2arr)" />
        );
      })}

      {/* Nodes */}
      {nodes.map((n) => {
        const pos = positions[n.id];
        if (!pos) return null;
        const { cx, cy } = pos;
        return (
          <g key={n.id}>
            <rect
              x={cx - BOX_W / 2} y={cy - BOX_H / 2}
              width={BOX_W} height={BOX_H} rx={6}
              fill="#ffffff" stroke={D2_NAVY} strokeWidth="1.5"
              filter="url(#nshadow)"
            />
            <text
              x={cx} y={cy}
              textAnchor="middle" dominantBaseline="middle"
              fontSize="13" fontWeight="700" fill={D2_NAVY}
              fontFamily="ui-sans-serif, system-ui, -apple-system, sans-serif"
            >
              {n.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Fallback graph from flat platform fields ─────────────────────────────────
function buildFallbackGraph(triggerPlatform, destinationPlatform) {
  if (!triggerPlatform && !destinationPlatform) return null;
  const nodes = [], edges = [];
  if (triggerPlatform) nodes.push({ id: "trigger", label: triggerPlatform, type: "trigger" });
  if (destinationPlatform) nodes.push({ id: "destination", label: destinationPlatform, type: "destination" });
  if (nodes.length === 2) edges.push({ from: "trigger", to: "destination" });
  return { nodes, edges };
}

// ─── Fullscreen Modal (React portal — renders at document.body) ───────────────
function FullscreenModal({ graph, wasmSvg, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: "rgba(15,23,42,0.82)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl overflow-auto p-10"
        style={{
          maxWidth: "90vw", maxHeight: "90vh", minWidth: 420,
          backgroundImage: `radial-gradient(circle, ${D2_NAVY}1a 1px, transparent 1px)`,
          backgroundSize: "22px 22px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition hover:opacity-80"
          style={{ background: D2_NAVY, color: "#fff" }}
          title="Close (Esc)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex items-center justify-center overflow-x-auto">
          {wasmSvg ? (
            <div style={{ lineHeight: 0, minWidth: 400, width: "100%" }}
              dangerouslySetInnerHTML={{ __html: wasmSvg }} />
          ) : (
            <div style={{ minWidth: 400, width: "100%" }}>
              <SVGFallback graph={graph} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

// ─── Main Component ───────────────────────────────────────────────────────────
const D2IntegrationDiagram = ({ flowGraph, triggerPlatform, destinationPlatform, fullHeight, onEdit }) => {
  const rawGraph = flowGraph || buildFallbackGraph(triggerPlatform, destinationPlatform);

  // Normalise once: resolve edge IDs + auto-connect isolated nodes
  const graph = useMemo(() => normaliseGraph(rawGraph), [rawGraph]); // eslint-disable-line react-hooks/exhaustive-deps

  const [wasmSvg, setWasmSvg] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const attempted = useRef(false);

  // Try D2 WASM upgrade silently in background
  useEffect(() => {
    if (!graph?.nodes?.length || attempted.current) return;
    attempted.current = true;

    const script = buildD2Script(graph);
    if (!script) return;

    let dead = false;
    (async () => {
      try {
        const { D2 } = await import("@terrastruct/d2");
        if (dead) return;
        const result = await new D2().compile({ input: script });
        if (dead || !result?.svg) return;
        const clean = result.svg
          .replace(/width="[^"]*"/, 'width="100%"')
          .replace(/height="[^"]*"/, 'height="auto"')
          .replace(/style="background:[^"]*"/, "");
        setWasmSvg(clean);
      } catch { /* keep SVG fallback silently */ }
    })();
    return () => { dead = true; };
  }, [graph]);

  if (!graph) return null;

  return (
    <>
      {fullscreen && (
        <FullscreenModal graph={graph} wasmSvg={wasmSvg} onClose={() => setFullscreen(false)} />
      )}

      <div className={`w-full overflow-hidden relative ${fullHeight ? 'h-full flex flex-col' : 'rounded-xl border border-[#d0d8f0]'}`}
        style={{ background: "#ffffff" }}>

        {/* Top-right overlay buttons */}
        <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-2">
          {onEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="w-7 h-7 rounded-lg flex items-center justify-center transition hover:bg-black/5"
              style={{ background: `${D2_NAVY}18`, color: D2_NAVY }}
              title="Edit Integration Settings"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                  d="M15.232 5.232l3.536 3.536M9 13l-4 1 1-4L17.5 4a2.121 2.121 0 013 3L9 13z" />
              </svg>
            </button>
          )}

          {/* Fullscreen button */}
          <button
            onClick={(e) => { e.stopPropagation(); setFullscreen(true); }}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition hover:opacity-80"
            style={{ background: `${D2_NAVY}18`, color: D2_NAVY }}
            title="Expand to fullscreen"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
            </svg>
          </button>
        </div>

        {/* Diagram body — full width, horizontal scroll for wide graphs */}
        <div
          className={`overflow-x-auto ${fullHeight ? 'flex-1 flex items-center justify-center' : ''}`}
          style={{
            background: "#ffffff",
            backgroundImage: `radial-gradient(circle, ${D2_NAVY}1a 1px, transparent 1px)`,
            backgroundSize: "22px 22px",
          }}
        >
          <div className="px-6 py-6 flex justify-center items-center" style={{ minHeight: fullHeight ? '100%' : 180, minWidth: "fit-content", width: "100%" }}>
            {wasmSvg ? (
              <div style={{ lineHeight: 0, width: "100%", minWidth: 300 }}
                dangerouslySetInnerHTML={{ __html: wasmSvg }} />
            ) : (
              <div style={{ width: "100%", minWidth: 300 }}>
                <SVGFallback graph={graph} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default D2IntegrationDiagram;
